import { createI18n, type I18nOptions, type DefineLocaleMessage } from 'vue-i18n'
import { nextTick } from 'vue'
import { default as svMessages } from '../locales/sv.json'
import { default as enMessages } from '../locales/en.json'
import * as vuetifyLocales from 'vuetify/locale'

export type MessageSchema = typeof svMessages

declare module 'vue-i18n' {
  export interface DefineLocaleMessage extends MessageSchema {
    $vuetify?: Record<string, unknown>
  }
}

const localeImports = import.meta.glob<DefineLocaleMessage>('../locales/*.json', { import: 'default' })

export const supportedLocales = Object.keys(localeImports).map((path) => path.split('/').at(-1)?.replace('.json', ''))

export async function setLocale(locale: string) {
  const vuetify = await import('./vuetify')
  const survicate = await import('./survicate')
  if (!i18n.global.availableLocales.includes(locale)) {
    if (!supportedLocales.includes(locale)) {
      throw new TypeError(`Unsupported locale requested ${locale}`)
    }
    console.log('Loading locale', locale)

    const importPath = `../locales/${locale}.json`
    const importFn = localeImports[importPath]
    if (importFn == null) {
      throw new TypeError(`Locale file not found: ${importPath}`)
    }
    const messages = await importFn()

    const vuetifyLocale = Object.hasOwn(vuetifyLocales, locale)
      ? (vuetifyLocales as Record<string, Record<string, unknown>>)[locale]
      : {}
    const messagesVuetify = messages.$vuetify ?? {}

    i18n.global.setLocaleMessage(locale, {
      ...messages,
      $vuetify: {
        ...vuetifyLocale,
        ...messagesVuetify,
      },
    })
  }

  await nextTick()

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  if (i18n.mode === 'legacy') {
    ;(i18n.global.locale as unknown as string) = locale
  } else {
    i18n.global.locale.value = locale
  }
  localStorage.setItem('locale', locale)
  vuetify.default.locale.current.value = locale
  survicate.setSurvicateLocale(locale)
}

const options: I18nOptions = {
  // Composition mode is REQUIRED by Vuetify 4's createVueI18nAdapter.
  legacy: false,
  // Keeps legacy Options API code working unchanged: this.$t(...) and
  // this.$i18n.locale stay available for src_old components.
  globalInjection: true,
  locale: localStorage.getItem('locale') ?? import.meta.env.VITE_APP_I18N_LOCALE ?? 'sv',
  fallbackLocale: import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE ?? 'en',
  missingWarn: false,
  fallbackWarn: false,
  messages: {
    // $vuetify merged eagerly (customer adaptation): the customer locale files
    // don't carry most of the app keys ada-app-ui's key map relies on, so
    // Vuetify's built-in strings are the fallback for unmapped internal keys.
    sv: { ...svMessages, $vuetify: vuetifyLocales.sv },
    en: { ...enMessages, $vuetify: vuetifyLocales.en },
  },
}

const i18n = createI18n<false, typeof options>(options)

void setLocale(localStorage.getItem('locale') ?? import.meta.env.VITE_APP_I18N_LOCALE ?? 'sv')

export default i18n
